/**
 * The one channel from a portal page to the mobile app that hosts it.
 *
 * The app opens a form on this portal inside a WebView. `react-native-webview`
 * injects `window.ReactNativeWebView` into that page and into no other, so its
 * presence is what "inside the app" means — in a browser every call here is a
 * no-op on its first line.
 *
 * What crosses is a HINT about when to refetch, never a fact about the form:
 * no answers, and no authority over state. The app re-reads the server, which
 * says what actually happened. That is also why losing a message is cheap — it
 * costs one stale list, while the answers are stored either way.
 *
 * The vocabulary is the server's (`deriveState` in
 * `blhWebServer/src/forms/formSubmissionEvents.js`), so the app reads this and
 * `form_submission_changed` the same way.
 */

export const FORM_RESULT_MESSAGE = "form_result";

/** The states a page can report. Only the ones the patient can cause here. */
export const FORM_RESULT_STATES = Object.freeze({
  draft: "draft",
  completed: "completed",
});

/** Is this page inside the mobile app's WebView? */
export function isInMobileApp() {
  return typeof window !== "undefined" && !!window.ReactNativeWebView;
}

/**
 * Tell the app a form reached a new state, the moment it happens.
 *
 * `fsid` names the form when the page knows it. A link the server REFUSED
 * names nothing — the refusal is deliberately the same for every submission,
 * so it cannot be used to probe ids — and then the message goes without one:
 * the WebView was opened for exactly one form, and the app knows which.
 *
 * Never throws: a failure here must not turn a stored submission into an
 * error the patient sees.
 */
export function reportFormResultToMobileApp({fsid = null, state}) {
  if (!isInMobileApp()) return false;
  if (!Object.values(FORM_RESULT_STATES).includes(state)) return false;

  const message = {type: FORM_RESULT_MESSAGE, state};
  if (fsid) message.fsid = fsid;

  try {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return true;
  } catch (error) {
    console.warn("Could not notify the app of the form result", error);
    return false;
  }
}
